import { createStore } from "vuex"
import createPersistedState from "vuex-persistedstate"

const vuexSession = createPersistedState({
  storage: window.sessionStorage,
  reducer: state => ({
    user: state.user,
    search_results: state.search_results,
    engineers: state.engineers,
    customers: state.customers,
    suspended_customers: state.suspended_customers,
    orders: state.orders,
    engineer_orders: state.engineer_orders,
    products: state.products,
    product_categories: state.product_categories,
    pending_holidays: state.pending_holidays,
    pests: state.pests,
    stock_updates: state.stock_updates,
    holiday_requests: state.holiday_requests,
    materials: state.materials,
    staff: state.staff,
    vehicles: state.vehicles,
    isVehicleInspected: state.isVehicleInspected,
    holidays: state.holidays,
    configurations: state.configurations,
    vehicleInspectionFilters: state.vehicleInspectionFilters
  })
})

export default createStore({
  state: {
    menuVisible: false,
    user: null,
    materials: [],
    engineers: [],
    customers: [],
    suspended_customers: [],
    staff: [],
    vehicles: [],
    pests: null,
    orders: [],
    engineer_orders: [],
    products: [],
    product_categories: [],
    holiday_requests: [],
    pending_holidays: [],

    stock_updates: [],
    search_results: [],
    search_query: {},

    customerTypeFilter: "",
    customerTextFilter: "",
    suspendedTextFilter: "",
    customerEngFilter: "",

    schedule_scope: "Day",
    schedule_engineer: null,
    schedule_date: null,
    schedule_week: null,

    flatpickrconfig: {
        // https://flatpickr.js.org/options/
        altFormat: "d/m/Y",
        altInput: true,
        dateFormat: "Z",
        allowInput: true
    },
    futureflatpickrconfig: {
        // https://flatpickr.js.org/options/
        minDate: new Date(),
        altFormat: "d/m/Y",
        altInput: true,
        dateFormat: "Z",
        allowInput: true
    },

    isVehicleInspected: false,
    configurations: {},
    vehicleInspectionFilters: {
      vehicleFilter: null,
      fromDate: "",
      toDate: ""
    },
    vehicleInspectionReturnFromForm: false,

    dialogOpen: false,
    dialogTitle: '',
    dialogMessage: '',
    dialogIcon: 'mdi-alert-circle',
    dialogType: 'info', // info | confirm
    dialogOnConfirm: null,
    dialogOnClose: null
  },

  mutations: {
    setIsVehicleInspected(state, value) {
      state.isVehicleInspected = value;
    },
    setVehicleInspectionFilters(state, filters) {
      state.vehicleInspectionFilters = { ...filters };
    },
    setVehicleInspectionReturnFromForm(state, value) {
      state.vehicleInspectionReturnFromForm = value;
    },
    setUser: (state, user) => (state.user = user),
    setSearchResults: (state, search_results) => (state.search_results = JSON.parse(JSON.stringify(search_results))),
    setConfigurations: (state, configurations) => (state.configurations = JSON.parse(JSON.stringify(configurations))),
    setSearchQuery: (state, search_query) => (state.search_query = JSON.parse(JSON.stringify(search_query))),
    setMaterials: (state, materials) => (state.materials = materials),
    setEngineers: (state, engineers) => (state.engineers = engineers),
    setVehicles: (state, vehicles) => (state.vehicles = vehicles),
    setCustomers: (state, customers) => {
      customers.forEach(customer => {
        customer._id = customer.account_number || customer.job_id
        customer.name = customer.surname || customer.customer_name
      })
      state.customers = customers
    },
    setSuspendedCustomers: (state, customers) => {
      customers.forEach(customer => {
        customer._id = customer.account_number || customer.job_id
        customer.name = customer.surname || customer.customer_name
      })
      state.suspended_customers = customers
    },
    setStaff: (state, staff) => (state.staff = staff),
    setPests: (state, pests) => (state.pests = pests),
    setProducts: (state, products) => (state.products = products),
    setProductCategories: (state, product_categories) => (state.product_categories = product_categories),
    setPendingHolidays: (state, pending_holidays) => (state.pending_holidays = pending_holidays),
    removePendingHoliday: (state, sortKey) => (
      state.pending_holidays = state.pending_holidays.filter(h => h.Sort_Key !== sortKey)
    ),
    addProduct: (state, product) => state.products.push(product),
    logOut: state => (state.user = null),
    setOrders: (state, orders) => (state.orders = orders),
    setEngineerOrders: (state, orders) => (state.engineer_orders = orders),
    setStockUpdates: (state, stock_updates) => (state.stock_updates = stock_updates),
    setHolidayRequests: (state, holiday_requests) => (state.holiday_requests = holiday_requests),
    removeHolidayRequest: (state, sortKey) => (
      state.holiday_requests = state.holiday_requests.filter(h => h.Sort_Key !== sortKey)
    ),
    customerTypeFilter: (state, val) => {
      state.customerTypeFilter = val
      if (val != "Contract") state.customerEngFilter = ""
    },
    customerTextFilter: (state, val) => (state.customerTextFilter = val),
    suspendedTextFilter: (state, val) => (state.suspendedTextFilter = val),
    customerEngFilter: (state, val) => (state.customerEngFilter = val),
    setScheduleScope: (state, schedule_scope) => (state.schedule_scope = schedule_scope),
    setScheduleEngineer: (state, schedule_engineer) => (state.schedule_engineer = schedule_engineer),
    setScheduleDate: (state, schedule_date) => (state.schedule_date = schedule_date),
    setScheduleWeek: (state, schedule_week) => (state.schedule_week = schedule_week),

    updateProduct(state, updatedProduct) {
        const index = state.products.findIndex(
            p => p.Sort_Key === updatedProduct.Sort_Key
        );

        if (index !== -1) {
            state.products.splice(index, 1, {
                ...state.products[index],
                ...updatedProduct
            });
        }
    },

    removeProduct(state, sortKey) {
      state.products = state.products.filter(
        product => product.Sort_Key !== sortKey
      );
    },

    OPEN_DIALOG(state, payload) {
      state.dialogOpen = true
      state.dialogTitle = payload.title
      state.dialogMessage = payload.message
      state.dialogIcon = payload.icon || 'mdi-alert-circle',
      state.dialogType = payload.type || 'info',
      state.dialogOnConfirm = payload.onConfirm || null,
      state.dialogOnClose = payload.onClose || null
    },

    CLOSE_DIALOG(state) {
      state.dialogOpen = false,
      state.dialogType = 'info',
      state.dialogOnConfirm = null,
      state.dialogOnClose = null
    }
  },

  actions: {
    showError({ commit }, payload) {
      if (typeof payload === "string") {
        payload = { message: payload }
      }

      commit('OPEN_DIALOG', {
        title: 'Error',
        message: payload.message,
        icon: 'mdi-alert-circle',
        onClose: payload.onClose || null
      })
    },

    showSuccess({ commit }, message) {
        commit('OPEN_DIALOG', {
            title: 'Success',
            message,
            icon: 'mdi-check-circle'
        });
    },

    confirm({ commit }, { title, message, onConfirm }) {
        commit('OPEN_DIALOG', {
            title,
            message,
            icon: 'mdi-help-circle',
            type: 'confirm',
            onConfirm
        });
    }, 

    closeDialog({ state, commit }) {
      if (state.dialogOnClose) {
        state.dialogOnClose()
      }

      commit('CLOSE_DIALOG')
    }
  },

  plugins: [vuexSession]
})
